/**
 * "Mit MyHours-Konto anmelden" (ADR-0001, Abschnitt 3): Authorization Code + PKCE über die
 * Cognito-Domain des MyBudget-Pools, der den MyHours-Pool als OIDC-Provider "MyHours" kennt.
 *
 * Die erhaltenen Tokens werden genau so abgelegt, wie `amazon-cognito-identity-js` sie selbst
 * speichert -- danach funktionieren `getCurrentIdToken()`, Token-Erneuerung und Abmelden für
 * föderierte und lokale Konten gleich.
 */

const DOMAIN = (import.meta.env.VITE_COGNITO_DOMAIN ?? '').replace(/\/$/, '');
const CLIENT_ID = import.meta.env.VITE_USER_POOL_CLIENT_ID;
const PKCE_KEY = 'mybudget.pkce';

/** Nur sichtbar, wenn die Föderation in dieser Umgebung eingerichtet ist. */
export function myHoursLoginEnabled(): boolean {
  return Boolean(DOMAIN) && import.meta.env.VITE_MYHOURS_LOGIN === 'true';
}

export function redirectUri(origin = window.location.origin): string {
  return `${origin}/auth/callback`;
}

function base64Url(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomString(length = 64): string {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return base64Url(bytes).slice(0, length);
}

export async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

export function authorizeUrl(params: {
  domain: string;
  clientId: string;
  redirectUri: string;
  challenge: string;
  state: string;
}): string {
  const q = new URLSearchParams({
    identity_provider: 'MyHours',
    response_type: 'code',
    client_id: params.clientId,
    redirect_uri: params.redirectUri,
    scope: 'openid email profile',
    code_challenge: params.challenge,
    code_challenge_method: 'S256',
    state: params.state,
  });
  return `${params.domain}/oauth2/authorize?${q}`;
}

/** Startet die Anmeldung: Prüfwerte merken, zur Cognito-Domain weiterleiten. */
export async function startMyHoursLogin(target: string): Promise<void> {
  const verifier = randomString(64);
  const state = randomString(32);
  sessionStorage.setItem(PKCE_KEY, JSON.stringify({ verifier, state, target }));
  window.location.assign(
    authorizeUrl({
      domain: DOMAIN,
      clientId: CLIENT_ID,
      redirectUri: redirectUri(),
      challenge: await pkceChallenge(verifier),
      state,
    }),
  );
}

export interface TokenSet {
  id_token: string;
  access_token: string;
  refresh_token?: string;
}

function usernameFromIdToken(idToken: string): string {
  const payload = idToken.split('.')[1] ?? '';
  const json = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as Record<
    string,
    unknown
  >;
  return String(json['cognito:username'] ?? json.sub ?? '');
}

/** Ablage im Format von amazon-cognito-identity-js (CognitoUser.cacheTokens). */
export function storeTokens(
  tokens: TokenSet,
  clientId = CLIENT_ID,
  storage: Storage = localStorage,
) {
  const username = usernameFromIdToken(tokens.id_token);
  const prefix = `CognitoIdentityServiceProvider.${clientId}`;
  storage.setItem(`${prefix}.LastAuthUser`, username);
  storage.setItem(`${prefix}.${username}.idToken`, tokens.id_token);
  storage.setItem(`${prefix}.${username}.accessToken`, tokens.access_token);
  if (tokens.refresh_token)
    storage.setItem(`${prefix}.${username}.refreshToken`, tokens.refresh_token);
  storage.setItem(`${prefix}.${username}.clockDrift`, '0');
}

/**
 * Rückkehr von Cognito: Prüfwert vergleichen, Code gegen Tokens tauschen, ablegen.
 * Liefert das ursprüngliche Ziel innerhalb der App.
 */
export async function completeMyHoursLogin(search: string): Promise<string> {
  const params = new URLSearchParams(search);
  const error = params.get('error_description') ?? params.get('error');
  if (error) throw new Error(`Anmeldung abgelehnt: ${error}`);
  const saved = JSON.parse(sessionStorage.getItem(PKCE_KEY) ?? 'null') as {
    verifier: string;
    state: string;
    target: string;
  } | null;
  sessionStorage.removeItem(PKCE_KEY);
  const code = params.get('code');
  if (!saved || !code || params.get('state') !== saved.state) {
    throw new Error('Die Anmeldung konnte nicht zugeordnet werden. Bitte erneut versuchen.');
  }
  const res = await fetch(`${DOMAIN}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: CLIENT_ID,
      code,
      redirect_uri: redirectUri(),
      code_verifier: saved.verifier,
    }),
  });
  if (!res.ok) throw new Error(`Anmeldung fehlgeschlagen (HTTP ${res.status}).`);
  storeTokens((await res.json()) as TokenSet);
  return saved.target;
}
